import { atom, GetRecoilValue } from 'recoil';
import isObject from 'lodash/isObject';
import sortBy from 'lodash/sortBy';

import * as K from 'common/constants';
import { exists } from 'common/functions';
import { PERMISSION_REQUIRING_FEATURE } from 'common/types';
import {
  AccessLevel,
  AccountData,
  DashboardV2,
  FeaturePermissions,
  Lab,
  Permission,
  SavedSearch,
  SavedSearchType,
  TeamData,
} from 'common/graphqlTypes';
import { getDomainFromEmail, numericStringCompare } from 'common/util/string';
import { recoilSelector } from 'common/util/recoil-selector';
import { recoilSelectorFamily } from 'common/util/recoil-selector-family';

// Permission hierarchy adapted from the "implies" methods in ApiToken.java. Contains permissions that apply to API keys, for completeness.
const PERMISSION_IMPLIES: Record<Permission, Permission[]> = {
  [Permission.limited]: [Permission.limited],
  [Permission.manageApiKeys]: [Permission.manageApiKeys],
  [Permission.logRead]: [Permission.limited, Permission.logRead],
  [Permission.logWrite]: [Permission.limited, Permission.logWrite],
  [Permission.configRead]: [Permission.limited, Permission.logRead, Permission.configRead],
  [Permission.configWrite]: [
    Permission.limited,
    Permission.logRead,
    Permission.configRead,
    Permission.configWrite,
    Permission.userConfigWrite,
  ],
  [Permission.userConfigWrite]: [
    Permission.limited,
    Permission.logRead,
    Permission.configRead,
    Permission.userConfigWrite,
  ],
  [Permission.xdrUser]: [
    Permission.limited,
    Permission.manageApiKeys,
    Permission.logRead,
    Permission.logWrite,
    Permission.userConfigWrite,
    Permission.configRead,
    Permission.configWrite,
    Permission.xdrUser,
  ],
  [Permission.fullAccess]: [
    Permission.limited,
    Permission.logRead,
    Permission.logWrite,
    Permission.configRead,
    Permission.configWrite,
    Permission.userConfigWrite,
    Permission.fullAccess,
  ],
  [Permission.owner]: [
    Permission.limited,
    Permission.logRead,
    Permission.logWrite,
    Permission.configRead,
    Permission.configWrite,
    Permission.userConfigWrite,
    Permission.fullAccess,
    Permission.owner,
  ],
  [Permission.master]: [
    Permission.limited,
    Permission.logRead,
    Permission.logWrite,
    Permission.configRead,
    Permission.configWrite,
    Permission.userConfigWrite,
    Permission.fullAccess,
    Permission.owner,
    Permission.master,
  ],
};

export type UserData = {
  account: AccountData;
  dashboardsV2: DashboardV2[];
  featurePermissions: FeaturePermissions;
  labs: Lab[];
  privateSearches: SavedSearch[];
  sharedSearches: SavedSearch[];
};

export const userDataState = atom<UserData>({
  key: K.RECOIL_ATOM_KEY.USER_DATA,
  default: {
    account: K.EMPTY_OBJECT as AccountData,
    dashboardsV2: K.EMPTY_ARRAY as DashboardV2[],
    featurePermissions: K.EMPTY_OBJECT as FeaturePermissions,
    labs: K.EMPTY_ARRAY as Lab[],
    privateSearches: K.EMPTY_ARRAY as SavedSearch[],
    sharedSearches: K.EMPTY_ARRAY as SavedSearch[],
  },
});

/**
 * Global state for account data
 */
export const accountDataState = recoilSelector<Partial<AccountData>>({
  key: K.RECOIL_SELECTOR_KEY.ACCOUNT_DATA,
  get: ({ get }) => {
    const userData = get(userDataState);
    return userData.account;
  },
});

export const accountDataReadyState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.ACCOUNT_DATA_READY,
  get: ({ get }) => {
    const accountData = get(accountDataState);
    return !!accountData && accountData !== K.EMPTY_OBJECT;
  },
});

/**
 * Global state for feature permissions
 */
export const featurePermissionsState = recoilSelector<Partial<FeaturePermissions>>({
  key: K.RECOIL_SELECTOR_KEY.FEATURE_PERMISSIONS,
  get: ({ get }) => {
    const userData = get(userDataState);
    return userData.featurePermissions;
  },
});

export const featurePermissionsReadyState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.FEATURE_PERMISSIONS_READY,
  get: ({ get }) => {
    const featurePermissions = get(featurePermissionsState);
    return !!featurePermissions && featurePermissions !== K.EMPTY_OBJECT;
  },
});

const createFeatureAccessibilityGetter =
  (featureName: PERMISSION_REQUIRING_FEATURE) =>
  ({ get }: { get: GetRecoilValue }) => {
    const featurePermissions = get(featurePermissionsState);

    return [AccessLevel.READ, AccessLevel.WRITE].includes(featurePermissions?.[featureName] || AccessLevel.DENY);
  };

export const searchAccessibleState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.SEARCH_ACCESSIBLE,
  get: createFeatureAccessibilityGetter(PERMISSION_REQUIRING_FEATURE.SEARCH),
});

export const dashboardsAccessibleState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.DASHBOARDS_ACCESSIBLE,
  get: createFeatureAccessibilityGetter(PERMISSION_REQUIRING_FEATURE.DASHBOARDS),
});

export const logsAccessibleState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.LOGS_ACCESSIBLE,
  get: createFeatureAccessibilityGetter(PERMISSION_REQUIRING_FEATURE.LOGS),
});

/**
 * Global state for labs
 */
export const labsState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.LABS,
  get: ({ get }) => {
    const userData = get(userDataState);
    return userData.labs;
  },
});

export const isLabEnabledState = recoilSelectorFamily({
  key: K.RECOIL_SELECTOR_KEY.IS_LAB_ENABLED,
  get:
    (labName: K.LAB) =>
    ({ get }) => {
      const labs = get(labsState);
      return Boolean(labs.find(({ name }) => labName === name)?.enabled);
    },
});

export const isManageGroupsEnabledState = recoilSelector({
  key: K.RECOIL_SELECTOR_KEY.IS_MANAGE_GROUPS_ENABLED,
  get: ({ get }) => {
    const isManageGroupsLabEnabled = get(isLabEnabledState(K.LAB.MANAGE_GROUPS));
    const { activeTeamPermission } = get(accountDataState);
    const isAbovePermissionThreshold =
      activeTeamPermission === Permission.fullAccess || activeTeamPermission === Permission.owner;
    return isManageGroupsLabEnabled && isAbovePermissionThreshold;
  },
});

export type SavedSearches = {
  [SavedSearchType.PRIVATE]: SavedSearch[];
  [SavedSearchType.SHARED]: SavedSearch[];
};

/**
 * Global state for private and shared searches
 */
export const savedSearchesState = recoilSelector<SavedSearches>({
  key: K.RECOIL_SELECTOR_KEY.SAVED_SEARCHES,
  get: ({ get }) => {
    const userData = get(userDataState);
    return {
      [SavedSearchType.PRIVATE]: [...userData.privateSearches].sort((a, b) => numericStringCompare(a.name, b.name)),
      [SavedSearchType.SHARED]: [...userData.sharedSearches].sort((a, b) => numericStringCompare(a.name, b.name)),
    };
  },
  set: ({ set, get }, newValue) => {
    const newSavedSearches = { ...(newValue as SavedSearches) };
    const userData = get(userDataState);
    set(userDataState, {
      ...userData,
      ...(newSavedSearches[SavedSearchType.PRIVATE] && { privateSearches: newSavedSearches[SavedSearchType.PRIVATE] }),
      ...(newSavedSearches[SavedSearchType.SHARED] && { sharedSearches: newSavedSearches[SavedSearchType.SHARED] }),
    });
  },
});

/**
 * Global state for the user's list of dashboards
 */
export const dashboardListState = recoilSelector<DashboardV2[]>({
  key: K.RECOIL_SELECTOR_KEY.DASHBOARD_LIST,
  get: ({ get }) => {
    const userData = get(userDataState);
    return [...userData.dashboardsV2].sort((a, b) => numericStringCompare(a.name, b.name));
  },
});

/**
 * Returns true if the user has the given permission, based on the hierarchy in PERMISSION_IMPLIES.
 *
 * @param permission    the permission to check for
 *
 * @returns true if the user has the given permission or higher
 */
export const hasPermissionState = recoilSelectorFamily<boolean, Permission>({
  key: K.RECOIL_SELECTOR_KEY.HAS_PERMISSION,
  get:
    (permission: Permission) =>
    ({ get }): boolean => {
      const accountData = get(accountDataState);
      const activeTeamPermission = accountData?.activeTeamPermission;
      const permsList = (activeTeamPermission && PERMISSION_IMPLIES[activeTeamPermission]) || [];
      return permsList.includes(permission);
    },
});

/**
 * Returns a record of {@see Permission} to true if the user has the permission
 * based on the hierarchy in {@see PERMISSION_IMPLIES}.
 */
export const permissionRecordState = recoilSelector<Record<Permission, boolean>>({
  key: K.RECOIL_SELECTOR_KEY.PERMISSION_RECORD,
  get: ({ get }) =>
    Object.values(Permission).reduce((record, permission) => {
      record[permission] = get(hasPermissionState(permission));
      return record;
    }, {} as Record<Permission, boolean>),
});

// Some teams include a name field for displayName, and other teams use emailAddress for this purpose
export type Team = TeamData & { displayName: string };
type MixedTeamData = TeamData | string | null | undefined;
/**
 * Get a {@see Team} object from either API version TODO: Remove once V2 is removed
 */
const getTeamFromV1AndV2 = (v1: MixedTeamData, v2: MixedTeamData): Team => {
  if (isObject(v1)) {
    const displayName = (v1 as TeamData)?.name ?? (v1 as TeamData)?.emailAddress;
    return { ...v1, displayName } as Team;
  }
  if (isObject(v2)) {
    const displayName = (v2 as TeamData)?.name ?? (v2 as TeamData)?.emailAddress;
    return { ...v2, displayName } as Team;
  }
  const emailAddress = (v1 as string) ?? '';
  return { displayName: emailAddress, emailAddress, id: emailAddress.toLowerCase() } as Team;
};

/**
 * Currently active {@see Team} for this user. TODO: simplify once V2 is removed
 */
export const activeTeamState = recoilSelector<Team>({
  key: K.RECOIL_SELECTOR_KEY.ACTIVE_TEAM,
  get: ({ get }) => {
    const accountData = get(accountDataState);
    return getTeamFromV1AndV2(accountData?.activeTeam, accountData?.activeTeamV2);
  },
});

/**
 * List of {@see Team}s available to this user TODO: simplify once V2 is removed
 */
export const teamsState = recoilSelector<Team[]>({
  key: K.RECOIL_SELECTOR_KEY.TEAMS,
  get: ({ get }) => {
    const accountData = get(accountDataState);
    const teamsV1 = accountData?.teams ?? [];
    const teamsV2 = accountData?.teamsV2 ?? [];
    const teams = [];
    const iterations = Math.max(teamsV1.length, teamsV2.length);
    for (let i = 0; i < iterations; i++) {
      teams.push(getTeamFromV1AndV2(teamsV1[i], teamsV2[i]));
    }
    return teams;
  },
});

/**
 * True if the user is viewing their own account.
 */
export const isViewingOwnAccountState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.IS_VIEWING_OWN_ACCOUNT,
  get: ({ get }) => {
    const accountData = get(accountDataState);
    const activeTeam = get(activeTeamState);
    return (
      activeTeam.id === accountData?.id ||
      activeTeam.emailAddress.toLowerCase() === (accountData?.email ?? '').toLowerCase()
    );
  },
});

/**
 * True if the user menu should display both a user menu display name for the team and the user's email.
 */
export const isTeamDisplayedState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.IS_TEAM_DISPLAYED,
  get: ({ get }) => {
    const accountData = get(accountDataState);
    const userEmail = accountData?.email ?? '';
    const isViewingOwnAccount = get(isViewingOwnAccountState);
    const activeTeam = get(activeTeamState);
    return !isViewingOwnAccount || activeTeam.displayName !== userEmail;
  },
});

/**
 * Name displayed at the top of the user menu.
 */
export const userMenuDisplayNameState = recoilSelector<string>({
  key: K.RECOIL_SELECTOR_KEY.USER_MENU_DISPLAY_NAME,
  get: ({ get }) => {
    const accountData = get(accountDataState);
    const userEmail = accountData?.email ?? '';

    const activeTeam = get(activeTeamState);
    const teamEmail = activeTeam.emailAddress;
    const teamName = activeTeam.name;

    const isSharedDomain = getDomainFromEmail(userEmail) === getDomainFromEmail(teamEmail);
    const teamNickname = isSharedDomain ? teamEmail.split('@')[0] : null;

    return teamName ?? teamNickname ?? teamEmail;
  },
});

/**
 * Available {@see Team}s for this user, not including the currently active team. The teams are returned in the order of
 * most recently used, which is the default sort order from the server.
 */
export const recentNonactiveTeamsState = recoilSelector<Team[]>({
  key: K.RECOIL_SELECTOR_KEY.RECENT_NONACTIVE_TEAMS,
  get: ({ get }) => {
    const activeTeam = get(activeTeamState);
    const teams = get(teamsState);
    return teams.filter((team) => team.id !== activeTeam.id);
  },
});

/**
 * Available {@see Team}s for this user, not including the currently active team. The teams are sorted alphabetically
 * by displayName.
 */
export const availableNonactiveTeamsState = recoilSelector<Team[]>({
  key: K.RECOIL_SELECTOR_KEY.AVAILABLE_NONACTIVE_TEAMS,
  get: ({ get }) => {
    const nonactiveTeams = get(recentNonactiveTeamsState);
    return sortBy(nonactiveTeams, ['displayName']);
  },
});

/**
 * All available {@see Team}s' email addresses, including the currently active team.
 */
export const availableAllTeamsEmailAddressesState = recoilSelector<string[]>({
  key: K.RECOIL_SELECTOR_KEY.AVAILABLE_ALL_TEAMS_EMAIL_ADDRESSES,
  get: ({ get }) => {
    const activeTeam = get(activeTeamState);
    const availableNonactiveTeams = get(availableNonactiveTeamsState);

    return [
      activeTeam.emailAddress.toLowerCase(),
      ...availableNonactiveTeams.map(({ emailAddress }) => emailAddress),
    ].filter(exists);
  },
});

/**
 * True if the user has access to multiple {@see Team}s.
 */
export const hasTeamsState = recoilSelector<boolean>({
  key: K.RECOIL_SELECTOR_KEY.HAS_TEAMS,
  get: ({ get }) => get(availableNonactiveTeamsState).length > 0,
});

/**
 * Short-term data retention period
 */
export const retentionPeriodState = recoilSelector<number>({
  key: K.RECOIL_SELECTOR_KEY.RETENTION_PERIOD,
  get: ({ get }) => {
    const accountData = get(accountDataState);
    return accountData?.planData?.retention ?? -1;
  },
});

export const hindsightRetentionState = atom<number>({
  key: K.RECOIL_ATOM_KEY.HINDSIGHT_RETENTION,
  default: 0,
});
